/**
 * TypeScript port of WebV5/Scripts/Approver/ExpenseApprovals/constants/expenseCategories.js
 *
 * Defines subcategory form-field configurations used to drive dynamic field
 * rendering in the Expense Detail panel (read-only approver view).
 *
 * Three fields not present in the original JS file are added here so that every
 * subcategory is consistent when rendered in the detail drawer:
 *   - ACCOUNT_CODE
 *   - CARD_CURRENCY_AMOUNT
 *   - SETTLE_DATE
 */

export const DynamoDBFieldNames = {
    AMOUNT: "Amount",
    CHARGE_DATE: "ChargeDate",
    CURRENCY: "Currency",
    DEPARTURE_DATE: "DepartureDate",
    CHECK_IN_DATE: "CheckInDate",
    LOCATION_FROM: "LocationFrom",
    LOCATION_TO: "LocationTo",
    LOCATION: "Location",
    PICKUP_ADDRESS: "PickupAddress",
    DROP_OFF_ADDRESS: "DropoffAddress",
    MERCHANT: "Merchant",
    PAYMENT: "Payment",
    PURPOSE: "Purpose",
    TICKET_NUMBER: "TicketNumber",
    PARTICIPANTS: "Participants",
    CLIENT: "Client",
    MILEAGE_LOCATION_FROM: "MilageLocationFrom",
    MILEAGE_LOCATION_TO: "MilageLocationTo",
    DISTANCE: "Distance",
    RATE: "Rate",
    DISTANCE_RATE: "DistanceRate",
    PICK_UP_DATE: "PickUpDate",
    INVOICE_NUMBER: "InvoiceNumber",
    // Added for the expense detail panel (not in the original JS file)
    ACCOUNT_CODE: "AccountCode",
    CARD_CURRENCY_AMOUNT: "CardCurrencyAmount",
    SETTLE_DATE: "SettleDate",
} as const;

export type DynamoDBFieldName = (typeof DynamoDBFieldNames)[keyof typeof DynamoDBFieldNames];

export const LAYOUT = {
    SINGLE_COLUMN: 1 as const,
    FULL_WIDTH: 2 as const,
    DOUBLE_HEIGHT: 2 as const,
};

export const expenseInputTypes = {
    TEXT_ENTRY: "TextEntry",
    PULL_DOWN: "PullDown",
    PULL_DOWN_WITH_CONDITIONAL_QUESTIONS: "PullDownWithConditionalQuestions",
    MONEY: "Money",
    AUTO_COMPLETE: "AutoComplete",
    PARTICIPANT: "Participant",
    PAYMENT_TYPE: "PaymentType",
} as const;

export type ExpenseInputType = (typeof expenseInputTypes)[keyof typeof expenseInputTypes];

export interface ExpenseFieldInputConfig {
    inputType: ExpenseInputType;
    format?: "Alphabet" | "AlphaNumeric" | "Numeric" | "Email" | "AlphaNumericWithSpace" | "AlphaNumericWithSpecialChar" | "DateTime";
    options?: string[];
    searchType?: "air" | "car" | "hotel" | "rail";
    /** Used by MONEY to persist selected currency alongside the numeric amount field. */
    currencyApiKey?: string;
}

export interface ExpenseCategoryField {
    placeholder: string;
    stateName: string;
    inputConfig: ExpenseFieldInputConfig;
    /** GraphQL response field name (camelCase). `null` = not yet available from the API. */
    apiKey: string | null;
    /** Legacy IDs used by the expense update payload. */
    expenseCategoryId?: number;
    expenseSubCategoryId?: number;
    layoutConfig: { span: 1 | 2; rowSpan?: 1 | 2 };
}

// ─── Private field builder ─────────────────────────────────────────────────────

// f(placeholder, stateName, span, apiKey, rowSpan?)
const f = (
    placeholder: string,
    stateName: string,
    span: 1 | 2,
    apiKey: string | null = null,
    rowSpan?: 1 | 2,
    inputConfig: ExpenseFieldInputConfig = { inputType: expenseInputTypes.TEXT_ENTRY, format: "AlphaNumericWithSpecialChar" },
): ExpenseCategoryField => ({
    placeholder,
    stateName,
    inputConfig,
    apiKey,
    layoutConfig: { span, ...(rowSpan ? { rowSpan } : {}) },
});

const N = DynamoDBFieldNames;

// ─── Shared field constants ───────────────────────────────────────────────────

const MERCHANT_FIELD = f("Merchant", N.MERCHANT, 2, "merchant", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "AlphaNumericWithSpecialChar",
});
const DESCRIBE_FIELD = f("Description", N.PURPOSE, 2, "description", 2, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "AlphaNumericWithSpecialChar",
});
const DEPARTURE_DATE_FIELD = f("Departure Date", N.DEPARTURE_DATE, 2, "departureDate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "DateTime",
});
const TICKET_NUMBER_FIELD = f("Ticket #", N.TICKET_NUMBER, 2, "ticketNumber", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "AlphaNumeric",
});
const PARTICIPANTS_FIELD = f("Participants", N.PARTICIPANTS, 2, "participants", undefined, {
    inputType: expenseInputTypes.PARTICIPANT,
});
const COMMON_MONEY_FIELD = f("Amount", N.AMOUNT, 1, "amount", undefined, {
    inputType: expenseInputTypes.MONEY,
    currencyApiKey: "currencyCode",
});
const PAYMENT_FIELD = f("Payment", N.PAYMENT, 1, null, undefined, {
    inputType: expenseInputTypes.PAYMENT_TYPE,
}); // complex object — rendered separately
const CHARGE_DATE_FIELD = f("Charge Date", N.CHARGE_DATE, 2, "chargeDate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "DateTime",
});
const LOCATION_FIELD = f("Location", N.LOCATION, 2, "location", undefined, {
    inputType: expenseInputTypes.AUTO_COMPLETE,
    searchType: "hotel",
});
const CLIENT_FIELD = f("Company Name", N.CLIENT, 2, "client", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "AlphaNumericWithSpecialChar",
});
const CHECK_IN_DATE_FIELD = f("Check-in Date", N.CHECK_IN_DATE, 2, "checkInDate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "DateTime",
});
const PICK_UP_DATE_FIELD = f("Pickup Date", N.PICK_UP_DATE, 2, "pickUpDate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "DateTime",
});
const PICKUP_ADDRESS_FIELD = f("Pickup", N.PICKUP_ADDRESS, 1, "pickupAddress", undefined, {
    inputType: expenseInputTypes.AUTO_COMPLETE,
    searchType: "car",
});
const DROP_OFF_ADDRESS_FIELD = f("Dropoff", N.DROP_OFF_ADDRESS, 1, "dropoffAddress", undefined, {
    inputType: expenseInputTypes.AUTO_COMPLETE,
    searchType: "car",
});
const DISTANCE_FIELD = f("Distance", N.DISTANCE, 1, "distance", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "Numeric",
});
const RATE_FIELD = f("Rate", N.RATE, 1, "rate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "Numeric",
});
const DISTANCE_RATE_FIELD = f("Distance Rate", N.DISTANCE_RATE, 1, "distanceRate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "Numeric",
});
const MILEAGE_FROM_FIELD = f("From", N.MILEAGE_LOCATION_FROM, 1, "milageLocationFrom", undefined, {
    inputType: expenseInputTypes.AUTO_COMPLETE,
    searchType: "car",
});
const MILEAGE_TO_FIELD = f("To", N.MILEAGE_LOCATION_TO, 1, "milageLocationTo", undefined, {
    inputType: expenseInputTypes.AUTO_COMPLETE,
    searchType: "car",
});

// Added for all subcategories for consistency in the detail drawer
const ACCOUNT_CODE_FIELD = f("Account Code", N.ACCOUNT_CODE, 1, "accountCode", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "AlphaNumericWithSpecialChar",
});
const CARD_CURRENCY_AMOUNT_FIELD = f("Card Currency Amount", N.CARD_CURRENCY_AMOUNT, 1, "cardCurrencyAmount", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "Numeric",
});
const SETTLE_DATE_FIELD = f("Settle Date", N.SETTLE_DATE, 1, "settleDate", undefined, {
    inputType: expenseInputTypes.TEXT_ENTRY,
    format: "DateTime",
});

// These 3 fields are appended to every subcategory's formFields so that the detail
// panel receives them for all categories.
const EXTRA_CONSISTENT_FIELDS: ExpenseCategoryField[] = [ACCOUNT_CODE_FIELD, CARD_CURRENCY_AMOUNT_FIELD, SETTLE_DATE_FIELD];

// ─── Per-category form field lists ────────────────────────────────────────────

function buildFirstScreen(merchantPlaceholder: string): ExpenseCategoryField[] {
    return [PAYMENT_FIELD, COMMON_MONEY_FIELD, { ...MERCHANT_FIELD, placeholder: merchantPlaceholder }, CHARGE_DATE_FIELD, ...EXTRA_CONSISTENT_FIELDS];
}

export const defaultFormFields: ExpenseCategoryField[] = [...buildFirstScreen("Merchant"), DESCRIBE_FIELD];

const AirFareFormFields: ExpenseCategoryField[] = [
    ...buildFirstScreen("Airline"),
    DEPARTURE_DATE_FIELD,
    TICKET_NUMBER_FIELD,
    f("From", N.LOCATION_FROM, 1, "locationFrom", undefined, { inputType: expenseInputTypes.AUTO_COMPLETE, searchType: "air" }),
    f("To", N.LOCATION_TO, 1, "locationTo", undefined, { inputType: expenseInputTypes.AUTO_COMPLETE, searchType: "air" }),
    DESCRIBE_FIELD,
];

const RailFormFields: ExpenseCategoryField[] = [
    ...buildFirstScreen("Rail Service"),
    DEPARTURE_DATE_FIELD,
    TICKET_NUMBER_FIELD,
    f("From", N.LOCATION_FROM, 1, "locationFrom", undefined, { inputType: expenseInputTypes.AUTO_COMPLETE, searchType: "rail" }),
    f("To", N.LOCATION_TO, 1, "locationTo", undefined, { inputType: expenseInputTypes.AUTO_COMPLETE, searchType: "rail" }),
    DESCRIBE_FIELD,
];

const ClientMealFormFields: ExpenseCategoryField[] = [...buildFirstScreen("Merchant"), PARTICIPANTS_FIELD, CLIENT_FIELD, LOCATION_FIELD, DESCRIBE_FIELD];

const EmployeeMealFormFields: ExpenseCategoryField[] = [...buildFirstScreen("Merchant"), PARTICIPANTS_FIELD, LOCATION_FIELD, DESCRIBE_FIELD];

const HotelStayFormFields: ExpenseCategoryField[] = [
    PAYMENT_FIELD,
    COMMON_MONEY_FIELD,
    MERCHANT_FIELD,
    CHARGE_DATE_FIELD,
    ...EXTRA_CONSISTENT_FIELDS,
    CHECK_IN_DATE_FIELD,
    LOCATION_FIELD,
    DESCRIBE_FIELD,
];

const CarRentalFormFields: ExpenseCategoryField[] = [
    PAYMENT_FIELD,
    COMMON_MONEY_FIELD,
    MERCHANT_FIELD,
    CHARGE_DATE_FIELD,
    ...EXTRA_CONSISTENT_FIELDS,
    PICK_UP_DATE_FIELD,
    PICKUP_ADDRESS_FIELD,
    DROP_OFF_ADDRESS_FIELD,
    DESCRIBE_FIELD,
];

const RideShareFormFields: ExpenseCategoryField[] = [...buildFirstScreen("Merchant"), LOCATION_FIELD, DESCRIBE_FIELD];

const ServiceFeesFormFields: ExpenseCategoryField[] = [...buildFirstScreen("Name of Provider"), DESCRIBE_FIELD];

export const MileageAddressFormFields: ExpenseCategoryField[] = [
    MILEAGE_FROM_FIELD,
    MILEAGE_TO_FIELD,
    DISTANCE_FIELD,
    CHARGE_DATE_FIELD,
    RATE_FIELD,
    DISTANCE_RATE_FIELD,
    COMMON_MONEY_FIELD,
    DESCRIBE_FIELD,
    ...EXTRA_CONSISTENT_FIELDS,
];

// ─── Category / subcategory config types ──────────────────────────────────────

export interface ExpenseSubCategoryConfig {
    subCategoryId: number;
    name: string;
    formFields: ExpenseCategoryField[];
}

export interface ExpenseCategoryConfig {
    categoryId: number;
    groupName: string;
    subCategories: ExpenseSubCategoryConfig[];
}

export const MILEAGE_LEGACY_ID = 15;
export const OTHER_LEGACY_ID = 23;

export function isMileage(legacyId: number | null | undefined): boolean {
    return legacyId === MILEAGE_LEGACY_ID;
}

export function isOther(legacyId: number | null | undefined): boolean {
    return legacyId === OTHER_LEGACY_ID;
}

/** Mileage and Other can never be deactivated and their names can never be edited. */
export function isProtectedCategory(legacyId: number | null | undefined): boolean {
    return isMileage(legacyId) || isOther(legacyId);
}

// ─── Master categories list ───────────────────────────────────────────────────

const RAW_EXPENSE_CATEGORIES: ExpenseCategoryConfig[] = [
    {
        categoryId: 1,
        groupName: "Business Travel",
        subCategories: [
            { subCategoryId: 1, name: "Airfare", formFields: AirFareFormFields },
            { subCategoryId: 2, name: "Car Rental", formFields: CarRentalFormFields },
            { subCategoryId: 3, name: "Hotel Stay", formFields: HotelStayFormFields },
            { subCategoryId: 4, name: "Rail", formFields: RailFormFields },
            { subCategoryId: 6, name: "Ride Share", formFields: RideShareFormFields },
            { subCategoryId: 7, name: "Service Fees", formFields: ServiceFeesFormFields },
            { subCategoryId: 8, name: "Airport Parking", formFields: defaultFormFields },
            { subCategoryId: 19, name: "Fuel/Gas", formFields: defaultFormFields },
        ],
    },
    {
        categoryId: 4,
        groupName: "General",
        subCategories: [
            { subCategoryId: 32, name: "Advertising", formFields: defaultFormFields },
            { subCategoryId: 29, name: "Client Entertainment", formFields: defaultFormFields },
            { subCategoryId: 34, name: "Client Event", formFields: defaultFormFields },
            { subCategoryId: 33, name: "Conference", formFields: defaultFormFields },
            { subCategoryId: 30, name: "Employee Entertainment", formFields: defaultFormFields },
            { subCategoryId: 25, name: "Foreign Exchange", formFields: defaultFormFields },
            { subCategoryId: 28, name: "Gifts", formFields: defaultFormFields },
            { subCategoryId: 35, name: "Hardware & Hosting", formFields: defaultFormFields },
            { subCategoryId: 20, name: "Internet/Online Fees", formFields: defaultFormFields },
            { subCategoryId: 31, name: "Marketing", formFields: defaultFormFields },
            { subCategoryId: 21, name: "Mobile Phones", formFields: defaultFormFields },
            { subCategoryId: 22, name: "Office Supplies", formFields: defaultFormFields },
            { subCategoryId: 23, name: "Other", formFields: defaultFormFields },
            { subCategoryId: 24, name: "Software", formFields: defaultFormFields },
            { subCategoryId: 27, name: "Tips", formFields: defaultFormFields },
            { subCategoryId: 26, name: "Utilities", formFields: defaultFormFields },
        ],
    },
    {
        categoryId: 2,
        groupName: "Meals",
        subCategories: [
            { subCategoryId: 13, name: "Client Meal", formFields: ClientMealFormFields },
            { subCategoryId: 9, name: "Employee Meal", formFields: EmployeeMealFormFields },
            { subCategoryId: 12, name: "Office Snacks", formFields: defaultFormFields },
        ],
    },
];

function attachLegacyIdsToFields(categories: ExpenseCategoryConfig[]): ExpenseCategoryConfig[] {
    return categories.map((category) => ({
        ...category,
        subCategories: category.subCategories.map((subCategory) => ({
            ...subCategory,
            formFields: subCategory.formFields.map((field) => ({
                ...field,
                expenseCategoryId: category.categoryId,
                expenseSubCategoryId: subCategory.subCategoryId,
            })),
        })),
    }));
}

export const ExpenseCategories: ExpenseCategoryConfig[] = attachLegacyIdsToFields(RAW_EXPENSE_CATEGORIES);

/** Set of subCategoryIds (legacy IDs) whose form fields include a PARTICIPANT input */
export const PARTICIPANT_LEGACY_IDS: ReadonlySet<number> = new Set(
    RAW_EXPENSE_CATEGORIES.flatMap((cat) =>
        cat.subCategories
            .filter((sub) => sub.formFields.some((f) => f.inputConfig.inputType === expenseInputTypes.PARTICIPANT))
            .map((sub) => sub.subCategoryId),
    ),
);

export function getExpenseCategoryIdsBySubCategoryId(subCategoryId: number | null): { expenseCategoryId: number; expenseSubCategoryId: number } | null {
    if (subCategoryId == null) return null;

    for (const category of ExpenseCategories) {
        for (const subCategory of category.subCategories) {
            if (subCategory.subCategoryId === subCategoryId) {
                return {
                    expenseCategoryId: category.categoryId,
                    expenseSubCategoryId: subCategory.subCategoryId,
                };
            }
        }
    }

    return null;
}

/** Maps admin categoryGroup values to legacy parent expense category IDs. */
export function getExpenseCategoryIdByGroupName(categoryGroup: string | null | undefined): number | null {
    if (!categoryGroup) return null;

    const normalized = categoryGroup.trim().toLowerCase();
    for (const category of ExpenseCategories) {
        if (category.groupName.trim().toLowerCase() === normalized) {
            return category.categoryId;
        }
    }

    return null;
}

/**
 * Resolves the legacy parent expenseCategoryId for updateExpense.
 * Subcategory is always sent as ULID (category.categoryId).
 *
 * When legacyId maps to a static subcategory whose parent disagrees with categoryGroup,
 * categoryGroup wins so API-only categories with stale legacy IDs still resolve correctly.
 */
export function resolveExpenseCategoryId(category: { categoryGroup: string | null; legacyId: number | null }): number | null {
    const fromGroup = getExpenseCategoryIdByGroupName(category.categoryGroup);

    if (category.legacyId != null) {
        const mapped = getExpenseCategoryIdsBySubCategoryId(category.legacyId);
        if (mapped) {
            if (fromGroup != null && fromGroup !== mapped.expenseCategoryId) {
                return fromGroup;
            }
            return mapped.expenseCategoryId;
        }
    }

    return fromGroup;
}
